import React, { useState } from 'react';
import * as Styled from './styled';
import { useLocale } from 'locale';
import { BaseCarousel } from 'ds/components/carousels/base-carousel';
import BlogCard from 'components/blog-card';
import GlassesIcon from 'assets/svg/glasses.svg';
import { Article } from 'store/sagas/apiService/types';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { getDirection } from 'components/blog-carousel/helper';
import { noop } from 'lodash';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver';

export interface DeveloperBlogSectionProps {
  articles: Article[];
  onCarouselScroll?: (d: 'left' | 'right', isMobile: boolean) => void;
  carouselOnly?: boolean;
}

export const BlogSection: React.FC<DeveloperBlogSectionProps> = ({ articles, onCarouselScroll = noop, carouselOnly }) => {
  const { t } = useLocale();
  const [ slideIndex, setSlideIndex ] = useState(0);
  const carouselSectionNode = React.useRef<HTMLDivElement>(null);
  const mediaBreakpoint = useScreenBreakpoint();
  const isMobile = mediaBreakpoint <= 2;
  const onCarouselSlideChange = (idx: number) => {
    onCarouselScroll(getDirection(slideIndex, idx), isMobile);
    setSlideIndex(idx);
  };

  const [ sectionInView, setSectionInView ] = React.useState(false);
  useIntersectionObserver(([ entry ]) => {
    setSectionInView(entry.isIntersecting);
  }, [ carouselSectionNode ], { threshold: 1 });

  return (
    <Styled.Root ref={carouselSectionNode} data-auto="articles-carousel">
      {carouselOnly ? null : (
        <Styled.Header>
          <Styled.InfoWrapper>
            <GlassesIcon height={40} width={40} />
            <Styled.InfoBox>
              <Styled.Title data-auto="articles-carousel-title" weight="bold">{t('developerPage.blog.title')}</Styled.Title>
              <Styled.SubTitle data-auto="articles-carousel-subtitle">{t('developerPage.blog.subtitle')}</Styled.SubTitle>
            </Styled.InfoBox>
          </Styled.InfoWrapper>
        </Styled.Header>
      )}
      <BaseCarousel
        hideShadow
        entryContentDXOverride={24}
        hideControls={isMobile}
        onSlideIndexChange={onCarouselSlideChange}
        activeCarouselSlide={slideIndex}
      >
        {articles.map((el, k, arr) => (
          <BlogCard
            key={k}
            articleId={el.id}
            sectionInView={sectionInView}
            rootNode={carouselSectionNode}
            title={el.title}
            imageUrl={el.image}
            developerId={el.owner.id}
            developerName={el.owner.name}
            postUrl={el.link}
            withLeftMargin={k !== arr.length - 1}
          />
        ))}
      </BaseCarousel>
    </Styled.Root>
  );
};
